/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Segmented-button styling layer (CRCB2B-642), from Figma `Segmented Button` (`1338:12311`).
 *
 * Shipped as a **styling layer rather than a component welded to one primitive**, because the
 * right semantic wrapper depends on behavior that differs per consumer, and Figma shows three
 * uses that do not agree: an `All / Mountain / Road` catalog filter, a grid/list view
 * switcher, and a `Crank Arm Length / Crankset` selector.
 *
 * `ui/segmented-button.tsx` binds this to Radix ToggleGroup `type="single"`, which is the
 * default and the only binding built. See that file for why not Tabs.
 *
 * ## Measured treatment
 *
 * | Part | Figma | Treatment |
 * |---|---|---|
 * | container | `1267:15035` | 48px, 1px `--border` #d6d6d6, `p-1`, full pill |
 * | selected | `1267:15017` | `--primary` #141414 fill, white label |
 * | unselected | `1267:15026` | no fill, `--muted-foreground` #585858 label, #4a4a4a icon |
 * | unselected hover | `6855:36582` | `--accent-hover` #d6d6d6 fill, `--foreground` label |
 *
 * Segments are 40px inside the 48px container — the 4px container padding is what makes the
 * selected pill float inside the outer pill rather than touching it. Label is 18px at weight
 * 500 with 3% tracking; the icon is 24px.
 *
 * Note the unselected icon (#4a4a4a) is deliberately **darker** than its label (#585858).
 *
 * ## Radius comes from a scoped token override, never `rounded-full`
 *
 * The pill is `[--ui-radius:100px]` on the container plus `rounded-ui` on both container and
 * segment. Custom properties inherit, so one override on the container reaches the segments —
 * which is also why a segment must not hard-code its own radius. Writing `rounded-full`
 * directly would violate the shape-token rules and, worse, would not respond to a re-theme.
 *
 * Any suppressed shadow in here is written `0 0 #0000`, never `none`: Tailwind composes ring
 * and shadow into one `box-shadow`, where `none` is illegal as a list item and silently voids
 * the whole declaration including the focus ring.
 */
/**
 * The control is **horizontal only**. An earlier draft exposed a vertical arrangement,
 * reading Figma's `Segmented Button Item` frame (`1999:20485`) as a vertical variant — it is
 * not, it is the item's state matrix laid out vertically on the canvas. No vertical segmented
 * control is designed, and a stacked one cannot wear this treatment correctly anyway: the
 * outer pill's radius is wrong for a tall box. Removed rather than left as a derived guess.
 *
 * ## The 3px padding is arithmetic, not a magic number
 *
 * Figma's container is 48px tall with a 1px stroke and a 4px inset to its 40px items — and in
 * Figma the stroke sits *inside* that inset. Under CSS `border-box` the equivalent is a 1px
 * border plus **3px** padding: 1 + 3 + 40 + 3 + 1 = 48.
 *
 * `p-1` looked right and was not. It left 48 − 2 border − 8 padding = **38px** for a 40px
 * segment, a 2px overflow — which is what produced the stray vertical scrollbar reported in
 * review. `overflow-y-hidden` only hid that symptom; this is the fix at source. The overflow
 * rule stays regardless, because a single `overflow-x` value forces the other axis off
 * `visible` (per CSS, `visible` computes to `auto` when its counterpart is not), and being
 * explicit about the cross axis documents that coupling.
 *
 * ## Group-level focus ring (CRCB2B-777) — and ONLY the group, not the segments
 *
 * Radix's roving tabindex means Tab only ever lands on one segment at a time (arrow keys move
 * focus *within* the group — that's the correct `radiogroup` pattern, see
 * `ui/toggle-group.tsx`), but the container's own pill border gave no indication that the
 * group — or anything inside it — currently has focus. `has-[:focus-visible]:` (a real
 * `:has()` selector, supported since Tailwind v4) turns the container's border into a focus
 * ring whenever *any* descendant matches `:focus-visible`, without needing to know which
 * segment that is.
 *
 * Deliberately the GROUP's treatment only. Each segment already carries its own indication —
 * the sliding thumb for the selected one, `data-[state=off]:focus-visible:bg-accent-hover` for
 * whichever unselected one currently has roving focus (see the item variants below) — so a
 * ring on the segment ITSELF would be a second, redundant focus affordance stacked on the
 * first. Reviewed and rejected for exactly that reason (CRCB2B-777): the segment gets the fill,
 * the group gets the ring, and neither wears the other's.
 *
 * `border-ring` + `ring-ring/50` + `ring-[3px]` is not a bespoke treatment invented for this
 * component — it's the SAME "decided focus treatment" (TASK 6.5) that `ui/input.tsx`,
 * `ui/checkbox.tsx`, `ui/radio-group.tsx`, and `ui/switch.tsx` all already use: a border-color
 * swap plus a 3px ring outside it. Reusing it here rather than inventing an outline-based ring
 * keeps every focus indicator in the app visually identical, and sidesteps a problem that an
 * outline approach hits and this one doesn't (see below).
 *
 * `outline-none` above still matters for a different reason than usual: it isn't clearing a
 * browser default so much as documenting one Radix already forces. `ui/toggle-group.tsx`'s
 * root is a Radix `RovingFocusGroup`, which sets an inline `style="outline: none"` on the
 * group root itself — to suppress a stray native outline flashing on the group during
 * roving-tabindex focus transitions. Measured in a real browser (jsdom applies no stylesheet,
 * so this is invisible to every unit/snapshot test): an inline style outranks *any* stylesheet
 * rule on specificity grounds alone, so an `outline`-based ring here — however specific,
 * `:has()` included — would need `!important` just to exist at all. `border-color` and
 * `box-shadow` (which is what `ring-*` compiles to) are different properties from `outline`,
 * so Radix's inline style never touches them and this treatment needs no such fight.
 *
 * It also sidesteps this container's own `overflow-x-auto`/`overflow-y-hidden` (needed for the
 * horizontal-scroll variant, see the padding note above): `border-color` paints on the
 * element's own border, inside its box, so there's nothing to clip regardless of overflow.
 */
export const segmentedButtonContainerVariants = cva(
    // `relative` is load-bearing, not cosmetic: it makes the container the offsetParent the
    // sliding thumb is measured against, and the containing block it is positioned in.
    // `group/segmented` lets segments read `data-thumb=ready` off it.
    'group/segmented [--ui-radius:100px] relative flex h-12 w-fit flex-row items-center gap-0 overflow-x-auto overflow-y-hidden rounded-ui border border-border p-[3px] shrink-0 outline-none has-[:focus-visible]:border-ring has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px]'
);

export const segmentedButtonItemVariants = cva(
    [
        // `relative` puts the segment in the same paint order as the absolutely-positioned
        // thumb; because it comes later in the DOM it lands on top, so the label stays legible
        // over the sliding fill without needing a z-index.
        'relative inline-flex h-10 shrink-0 items-center justify-center gap-2 overflow-clip rounded-ui px-4 py-2',
        'text-lg leading-none font-medium tracking-[0.03em] whitespace-nowrap transition-colors',
        // No `focus-visible:outline-*` here, on purpose (CRCB2B-777): the GROUP wears the
        // ring (see `segmentedButtonContainerVariants` above), the SEGMENT wears the
        // `bg-accent-hover` fill below. Giving the segment its own ring on top of that fill
        // would be a second, redundant focus affordance for the same event — reviewed and
        // rejected. `outline-none` still clears the native default so no browser UA outline
        // sneaks in ahead of the fill.
        'outline-none',
        // Unselected resting: label one step lighter than the icon, per Figma.
        'text-muted-foreground [&_[data-slot=icon]]:text-input-foreground',
        // Unselected hover — scoped to `data-state=off` so it CANNOT reach the selected
        // segment (CRCB2B-87).
        //
        // These were previously unscoped, on the reasoning that the `data-[state=on]` rules
        // below "come last so the selected pill does not lighten under the pointer". That is
        // not how it resolved. `[&:hover_[data-slot=icon]]:text-foreground` compiles to
        // `.cls:hover [data-slot=icon]` — one class, one pseudo-class, one attribute — and
        // `data-[state=on]:[&_[data-slot=icon]]:text-primary-foreground` compiles to
        // `.cls[data-state=on] [data-slot=icon]` — one class, two attributes. **Equal
        // specificity**, so emitted order decided it, and Tailwind sorts by variant rather than
        // by the order these strings are listed in. The hover rule won.
        //
        // The visible bug: hovering the SELECTED segment turned its icon dark
        // (`text-foreground`) while the background stayed dark (`bg-primary-hover`, which does
        // out-specify `hover:bg-accent-hover`) — a dark glyph on a dark pill, i.e. the active
        // option vanished under the pointer. The label had the same equal-specificity collision;
        // it only went unnoticed because the designed use is icon-only.
        //
        // Scoping removes the collision instead of out-specifying it, which also means the
        // selected segment has no hover treatment at all — the intended behaviour, since it is
        // already the active choice. Radix guarantees `data-state` is `"on" | "off"` on every
        // ToggleGroupItem, so `off` is a reliable hook rather than an absence of `on`.
        'data-[state=off]:hover:bg-accent-hover data-[state=off]:hover:text-foreground',
        '[&[data-state=off]:hover_[data-slot=icon]]:text-foreground',
        // Unselected keyboard focus (CRCB2B-777) — same `--accent-hover` fill as the hover
        // treatment above, per Figma `6855:36581`: the design has no separate focus mock, it
        // reuses the hover state as the focus state. This is the segment's ONLY focus
        // affordance — no ring here, that lives on the group container instead (see
        // `segmentedButtonContainerVariants`), so the two don't stack.
        //
        // Scoped to `data-state=off` for the same reason as hover above — the selected segment
        // is already the active choice and must not restyle under focus either.
        'data-[state=off]:focus-visible:bg-accent-hover data-[state=off]:focus-visible:text-foreground',
        '[&[data-state=off]:focus-visible_[data-slot=icon]]:text-foreground',
        // Selected treatment. No longer competing with hover, per the note above.
        'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
        'data-[state=on]:[&_[data-slot=icon]]:text-primary-foreground',
        // NOTE there is deliberately no `data-[state=on]:hover:` rule. The selected segment does
        // not react to the pointer at all: it is already the active choice, so a hover
        // affordance suggests an action that changes nothing. It previously took
        // `bg-primary-hover` — still dark, so not the cause of the vanishing icon above, but
        // removed on the same decision (CRCB2B-87). Its removal is also what makes the
        // `hover:bg-transparent` thumb rule below unnecessary.
        //
        // Once the sliding thumb is carrying the fill, the segment must NOT paint its own —
        // otherwise the outgoing segment stays dark for the whole animation and the pill
        // appears to duplicate rather than move. The group selector is two compound selectors
        // deep, so it outranks the `data-[state=on]` rules above regardless of class order.
        //
        // The segment keeps its own fill until then, which is what makes the un-hydrated HTML,
        // a JS-disabled client, and jsdom all render a correct selected state.
        'group-data-[thumb=ready]/segmented:data-[state=on]:bg-transparent',
        // The label and icon colours still transition per segment, so the outgoing text fades
        // to muted while the incoming text goes white as the fill arrives underneath.
        'group-data-[thumb=ready]/segmented:data-[state=on]:text-primary-foreground',
        'disabled:pointer-events-none disabled:text-disabled-foreground',
    ].join(' '),
    {
        variants: {
            content: {
                /** Icon and label together. */
                default: '',
                /** Icon only — a 40px square, so it needs no inline padding. */
                icon: 'w-10 px-0',
                /** Text only. Nothing reserves space for an absent icon; the gap collapses. */
                text: '',
            },
        },
        defaultVariants: {
            content: 'default',
        },
    }
);

export type SegmentedButtonContent = NonNullable<VariantProps<typeof segmentedButtonItemVariants>['content']>;
